import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFinance } from '@/hooks/useFinance';
import { useMerchantClients } from '@/hooks/useMerchantClients';
import {
  formatMoney,
  inRange,
  monthlySeries,
  periodRange,
  type FinanceAlert,
  type Period,
} from '@/lib/finance';
import { buildFinanceAlerts } from '@/lib/financeAlerts';
import { REVENUE_SOURCE_META } from '@/lib/financeSync';
import { FinanceStatCard, FinanceSection, FinanceEmpty, PeriodFilter } from './components/FinanceUI';

/** Petit graphique à barres (revenus vs dépenses) sans dépendance externe. */
function BarChart({ data, currency }: { data: { label: string; revenue: number; expense: number }[]; currency: string }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.revenue, d.expense)));
  return (
    <div>
      <div className="flex items-end justify-between gap-2 h-40">
        {data.map((d) => (
          <div key={d.label} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div className="w-full flex items-end justify-center gap-1 h-32">
              <div
                className="w-3 sm:w-4 rounded-t bg-primary-500 transition-all"
                style={{ height: `${Math.max(2, (d.revenue / max) * 100)}%` }}
                title={`Revenus ${formatMoney(d.revenue, currency)}`}
              ></div>
              <div
                className="w-3 sm:w-4 rounded-t bg-accent-500 transition-all"
                style={{ height: `${Math.max(2, (d.expense / max) * 100)}%` }}
                title={`Dépenses ${formatMoney(d.expense, currency)}`}
              ></div>
            </div>
            <span className="text-[10px] text-foreground-400 truncate">{d.label}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary-500"></span>Revenus</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-accent-500"></span>Dépenses</span>
      </div>
    </div>
  );
}

export default function FinanceOverviewPage() {
  const finance = useFinance();
  const { clients: merchantClients } = useMerchantClients();
  const { currency, loading, error, transactions, expenses, totalCash, receivables, payables, accountBalances } = finance;

  const [period, setPeriod] = useState<Period>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const range = useMemo(() => periodRange(period, customStart, customEnd), [period, customStart, customEnd]);

  const periodRevenue = useMemo(
    () => transactions.filter((t) => t.direction === 'in' && inRange(t.occurred_at, range)).reduce((s, t) => s + Number(t.amount), 0),
    [transactions, range]
  );
  const periodExpenses = useMemo(
    () => expenses.filter((e) => inRange(e.occurred_at, range)).reduce((s, e) => s + Number(e.amount), 0),
    [expenses, range]
  );
  const profit = periodRevenue - periodExpenses;

  const series = useMemo(() => monthlySeries(transactions, expenses, 6), [transactions, expenses]);

  const alerts = useMemo<FinanceAlert[]>(() => {
    return buildFinanceAlerts(
      {
        invoices: finance.invoices,
        suppliers: finance.suppliers,
        supplierTransactions: finance.supplierTransactions,
        recurring: finance.recurring,
        accountBalances,
        credit: finance.credit ? { customers: finance.credit.customers, balances: finance.credit.balances } : null,
        expenses,
        supplierBalance: finance.supplierBalance,
        alertPrefs: finance.alertPrefs,
      },
      currency,
    );
  }, [finance, expenses, currency, accountBalances]);

  const pendingPayments = useMemo(
    () => finance.invoices.filter((i) => i.status === 'sent' || i.status === 'partially_paid').reduce((s, i) => s + Math.max(0, i.total - i.amount_paid), 0),
    [finance.invoices]
  );

  // Revenus du mois en cours, regroupés par source (produits, digital,
  // services, formations, rendez-vous, hébergement…).
  const monthRevenueBySource = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const map: Record<string, number> = {};
    transactions
      .filter((t) => t.direction === 'in' && (t.source === 'order' || t.source === 'reservation') && new Date(t.occurred_at) >= start)
      .forEach((t) => {
        const label = t.category || 'Autres revenus';
        map[label] = (map[label] || 0) + Number(t.amount);
      });
    return map;
  }, [transactions]);

  const monthRevenueTotal = useMemo(
    () => Object.values(monthRevenueBySource).reduce((s, v) => s + v, 0),
    [monthRevenueBySource]
  );

  // Facturation : total émis vs réellement encaissé sur la période choisie.
  const billing = useMemo(() => {
    const list = finance.invoices.filter(
      (i) => (i.doc_type || 'invoice') === 'invoice' && i.status !== 'cancelled' && inRange(i.issue_date, range),
    );
    const total = list.reduce((s, i) => s + Number(i.total), 0);
    const collected = list.reduce((s, i) => s + Number(i.amount_paid), 0);
    const pending = Math.max(0, total - collected);
    const byClient = new Map<string, { name: string; billed: number; collected: number }>();
    list.forEach((i) => {
      const name = (i.customer_name || 'Client sans nom').trim();
      const key = name.toLowerCase();
      const row = byClient.get(key) || { name, billed: 0, collected: 0 };
      row.billed += Number(i.total);
      row.collected += Number(i.amount_paid);
      byClient.set(key, row);
    });
    const topClients = Array.from(byClient.values()).sort((a, b) => b.billed - a.billed).slice(0, 5);
    return {
      total,
      collected,
      pending,
      rate: total > 0 ? (collected / total) * 100 : 0,
      count: list.length,
      topClients,
    };
  }, [finance.invoices, range]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
        <p className="text-foreground-600 mb-3 text-sm">{error}</p>
        <button onClick={finance.reload} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <PeriodFilter
          value={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }}
        />
        <div className="flex items-center gap-2">
          <Link to="/dashboard/finance/depenses" className="flex items-center gap-1.5 px-3.5 py-2 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Dépense
          </Link>
          <Link to="/dashboard/finance/revenus" className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Revenu
          </Link>
        </div>
      </div>

      {/* Alertes financières */}
      {alerts.length > 0 && (
        <FinanceSection
          title={`Alertes financières (${alerts.length})`}
          icon="ri-alarm-warning-line"
          action={<Link to="/dashboard/finance/parametres" className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">Configurer</Link>}
        >
          <div className="space-y-2">
            {alerts.slice(0, 6).map((a, i) => {
              const tone = a.severity === 'danger' ? 'bg-red-50 text-red-600 border-red-100' : a.severity === 'warning' ? 'bg-accent-50 text-accent-700 border-accent-100' : 'bg-secondary-50 text-secondary-700 border-secondary-100';
              const icon = a.severity === 'danger' ? 'ri-error-warning-line' : a.severity === 'warning' ? 'ri-alert-line' : 'ri-information-line';
              return (
                <div key={`${a.key}-${i}`} className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg border ${tone}`}>
                  <i className={`${icon} text-base flex-shrink-0`}></i>
                  <span className="flex-1 min-w-0 text-sm truncate">{a.message}</span>
                  {a.amount != null && <span className="text-sm font-semibold whitespace-nowrap">{formatMoney(a.amount, currency)}</span>}
                </div>
              );
            })}
          </div>
        </FinanceSection>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label="Revenus (période)" value={formatMoney(periodRevenue, currency)} icon="ri-arrow-down-circle-line" tone="primary" />
        <FinanceStatCard label="Dépenses (période)" value={formatMoney(periodExpenses, currency)} icon="ri-arrow-up-circle-line" tone="accent" />
        <FinanceStatCard label="Bénéfice estimé" value={formatMoney(profit, currency)} icon="ri-line-chart-line" tone={profit >= 0 ? 'secondary' : 'danger'} hint="Revenus − dépenses de la période" />
        <FinanceStatCard label="Trésorerie disponible" value={formatMoney(totalCash, currency)} icon="ri-wallet-3-line" tone="secondary" hint="Somme des comptes" />
        <FinanceStatCard label="Créances clients" value={formatMoney(receivables, currency)} icon="ri-user-received-line" tone="primary" hint="Ce que vos clients vous doivent" />
        <FinanceStatCard label="Dettes fournisseurs" value={formatMoney(payables, currency)} icon="ri-truck-line" tone="accent" hint="Ce que vous devez" />
        <FinanceStatCard label="Paiements en attente" value={formatMoney(pendingPayments, currency)} icon="ri-time-line" tone="secondary" hint="Factures non soldées" />
        <FinanceStatCard label="Trésorerie prévue" value={formatMoney(totalCash + pendingPayments + receivables - payables, currency)} icon="ri-funds-line" tone="primary" hint="Projection simple" />
      </div>

      {/* Facturé vs encaissé */}
      <FinanceSection
        title="Facturé vs réellement encaissé"
        icon="ri-file-text-line"
        action={
          <Link to="/dashboard/finance/factures" className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
            Voir les factures
          </Link>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <div className="grid grid-cols-3 gap-2.5 mb-4">
              <div className="rounded-lg bg-background-100 p-3">
                <p className="text-[11px] text-foreground-500">Total facturé</p>
                <p className="text-sm font-bold text-foreground-950 mt-0.5 truncate">{formatMoney(billing.total, currency)}</p>
              </div>
              <div className="rounded-lg bg-primary-50 p-3">
                <p className="text-[11px] text-primary-700">Encaissé</p>
                <p className="text-sm font-bold text-primary-800 mt-0.5 truncate">{formatMoney(billing.collected, currency)}</p>
              </div>
              <div className="rounded-lg bg-accent-50 p-3">
                <p className="text-[11px] text-accent-700">Reste à encaisser</p>
                <p className="text-sm font-bold text-accent-800 mt-0.5 truncate">{formatMoney(billing.pending, currency)}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-foreground-600">Total facturé</span>
                  <span className="font-semibold text-foreground-900">{formatMoney(billing.total, currency)}</span>
                </div>
                <div className="h-2.5 rounded-full bg-background-100 overflow-hidden">
                  <div className="h-full rounded-full bg-secondary-500" style={{ width: billing.total > 0 ? '100%' : '0%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-foreground-600">Réellement encaissé</span>
                  <span className="font-semibold text-primary-700">
                    {formatMoney(billing.collected, currency)} · {Math.round(billing.rate)} %
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-background-100 overflow-hidden">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, Math.max(0, billing.rate))}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-foreground-600">Reste à encaisser</span>
                  <span className="font-semibold text-accent-700">
                    {formatMoney(billing.pending, currency)} · {Math.round(100 - billing.rate)} %
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-background-100 overflow-hidden">
                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${Math.min(100, Math.max(0, 100 - billing.rate))}%` }}></div>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-foreground-400 mt-3 flex items-start gap-1">
              <i className="ri-information-line mt-0.5"></i>
              {billing.count === 0
                ? 'Aucune facture émise sur cette période.'
                : `${billing.count} facture(s) sur la période sélectionnée. L’encaissé correspond aux factures marquées payées.`}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
              <i className="ri-trophy-line text-accent-500"></i>
              Clients les plus facturés
            </p>
            {billing.topClients.length === 0 ? (
              <FinanceEmpty
                icon="ri-user-line"
                title="Aucun client facturé"
                hint="Créez une facture depuis l’onglet Factures pour voir le classement."
              />
            ) : (
              <div className="divide-y divide-background-200/70">
                {billing.topClients.map((c, index) => {
                  const rate = c.billed > 0 ? (c.collected / c.billed) * 100 : 0;
                  return (
                    <div key={`${c.name}-${index}`} className="flex items-center gap-3 py-2.5">
                      <span className="w-4 text-xs font-bold text-foreground-400">{index + 1}</span>
                      <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground-800 truncate">{c.name}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <div className="h-1.5 flex-1 rounded-full bg-background-100 overflow-hidden">
                            <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, Math.max(0, rate))}%` }}></div>
                          </div>
                          <span className="text-[10px] text-foreground-400 whitespace-nowrap">{Math.round(rate)} % payé</span>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{formatMoney(c.billed, currency)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </FinanceSection>

      {/* Revenus du mois par source */}
      <FinanceSection
        title="Revenus du mois par source"
        icon="ri-pie-chart-2-line"
        action={<span className="text-sm font-bold text-primary-700 whitespace-nowrap">{formatMoney(monthRevenueTotal, currency)}</span>}
      >
        {monthRevenueTotal <= 0 ? (
          <FinanceEmpty icon="ri-money-dollar-circle-line" title="Aucun revenu ce mois" hint="Les revenus payés de vos modules apparaîtront ici automatiquement." />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {REVENUE_SOURCE_META.map((meta) => {
              const amount = monthRevenueBySource[meta.label] || 0;
              return (
                <div key={meta.key} className="bg-background-50 border border-background-200/70 rounded-lg p-3.5 flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <i className={meta.icon}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500 truncate">{meta.label}</p>
                    <p className="text-sm font-bold text-foreground-950">{formatMoney(amount, currency)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <FinanceSection title="Revenus vs Dépenses (6 derniers mois)" icon="ri-bar-chart-2-line">
          {series.some((s) => s.revenue || s.expense) ? (
            <BarChart data={series} currency={currency} />
          ) : (
            <FinanceEmpty icon="ri-bar-chart-line" title="Pas encore de données" hint="Enregistrez vos premiers revenus et dépenses pour voir le graphique." />
          )}
        </FinanceSection>

        <FinanceSection title="Évolution du bénéfice" icon="ri-line-chart-line">
          {series.some((s) => s.profit) ? (
            <div className="space-y-2">
              {series.map((s) => {
                const maxAbs = Math.max(1, ...series.map((x) => Math.abs(x.profit)));
                const pct = (Math.abs(s.profit) / maxAbs) * 100;
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <span className="w-8 text-xs text-foreground-400">{s.label}</span>
                    <div className="flex-1 h-4 bg-background-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${s.profit >= 0 ? 'bg-primary-500' : 'bg-red-400'}`}
                        style={{ width: `${Math.max(3, pct)}%` }}
                      ></div>
                    </div>
                    <span className={`w-24 text-right text-xs font-medium ${s.profit >= 0 ? 'text-primary-700' : 'text-red-600'}`}>
                      {formatMoney(s.profit, currency)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <FinanceEmpty icon="ri-line-chart-line" title="Aucun bénéfice calculé" hint="Les données apparaîtront dès vos premières écritures." />
          )}
        </FinanceSection>

        <FinanceSection title="Créances vs Dettes" icon="ri-scales-3-line">
          <div className="space-y-4">
            {[
              { label: 'Créances clients', value: receivables, tone: 'bg-primary-500', to: '/dashboard/finance/credit' },
              { label: 'Dettes fournisseurs', value: payables, tone: 'bg-accent-500', to: '/dashboard/finance/fournisseurs' },
            ].map((row) => {
              const max = Math.max(1, receivables, payables);
              return (
                <Link key={row.label} to={row.to} className="block no-underline">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-foreground-700">{row.label}</span>
                    <span className="text-sm font-semibold text-foreground-950">{formatMoney(row.value, currency)}</span>
                  </div>
                  <div className="h-3 bg-background-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${row.tone}`} style={{ width: `${Math.max(3, (row.value / max) * 100)}%` }}></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </FinanceSection>

        <FinanceSection
          title="Meilleurs clients"
          icon="ri-trophy-line"
          action={<Link to="/dashboard/finance/clients" className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">Voir tous</Link>}
        >
          {merchantClients.length === 0 ? (
            <FinanceEmpty icon="ri-user-line" title="Aucun client" hint="Vos meilleurs clients apparaîtront ici dès leurs premières commandes." />
          ) : (
            <div className="divide-y divide-background-200/70">
              {merchantClients.slice(0, 5).map((c, i) => (
                <div key={c.clientId} className="flex items-center gap-3 py-2.5">
                  <span className="w-5 text-xs font-bold text-foreground-400">{i + 1}</span>
                  <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {(c.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0 text-sm text-foreground-800 truncate">
                    {c.name || (c.clientId.startsWith('guest:') ? c.clientId.slice(6) : `Client #${c.clientId}`)}
                  </span>
                  <span className="text-sm font-semibold text-primary-700 whitespace-nowrap">{formatMoney(c.totalSpent, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </FinanceSection>

        <FinanceSection
          title="Dernières opérations"
          icon="ri-history-line"
          action={<Link to="/dashboard/finance/rapports" className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">Voir les rapports</Link>}
        >
          {transactions.length === 0 && expenses.length === 0 ? (
            <FinanceEmpty icon="ri-inbox-line" title="Aucune opération" hint="Vos revenus et dépenses apparaîtront ici." />
          ) : (
            <div className="divide-y divide-background-200/70">
              {[
                ...transactions.slice(0, 5).map((t) => ({ id: `t${t.id}`, at: t.occurred_at, label: t.description || t.category || 'Transaction', amount: Number(t.amount), dir: t.direction, icon: 'ri-exchange-dollar-line' })),
                ...expenses.slice(0, 5).map((e) => ({ id: `e${e.id}`, at: e.occurred_at, label: e.description || e.category, amount: Number(e.amount), dir: 'out' as const, icon: 'ri-shopping-bag-3-line' })),
              ]
                .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
                .slice(0, 6)
                .map((row) => (
                  <div key={row.id} className="flex items-center gap-3 py-2.5">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${row.dir === 'in' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'}`}>
                      <i className={`${row.icon} text-sm`}></i>
                    </span>
                    <span className="flex-1 min-w-0 text-sm text-foreground-800 truncate">{row.label}</span>
                    <span className={`text-sm font-semibold whitespace-nowrap ${row.dir === 'in' ? 'text-primary-700' : 'text-foreground-700'}`}>
                      {row.dir === 'in' ? '+' : '−'} {formatMoney(row.amount, currency)}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </FinanceSection>
      </div>

      <p className="text-[11px] text-foreground-400 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5"></i>
        Zifek Finance est un outil de gestion, pas un système comptable certifié. Les montants affichés sont calculés à partir de vos données réelles ; les estimations sont
        clairement signalées.
      </p>
    </div>
  );
}